import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { mfaInitAuth, mfaNewPassword, mfaVerifyTotp, mfaResendPassword } from '../api';
import QRCode from 'qrcode';

type Step = 'enterTempPassword' | 'setNewPassword' | 'scanQrCode' | 'complete';

const MfaSetup: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [step, setStep] = useState<Step>('enterTempPassword');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [tempPassword, setTempPassword] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');

  // Shared session across steps
  const [session, setSession] = useState('');

  // Step 2 state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 3 state
  const [totpSecret, setTotpSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const handleInitAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setError('');
    setLoading(true);
    try {
      const result = await mfaInitAuth(id, tempPassword);
      setSession(result.session);
      if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
        setStep('setNewPassword');
      } else {
        setError(`Unexpected challenge: ${result.challenge}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to authenticate');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!id) return;
    setError('');
    setResendSuccess('');
    setResending(true);
    try {
      const result = await mfaResendPassword(id);
      setResendSuccess(result.message);
      if (result.tempPassword) {
        setTempPassword(result.tempPassword);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend temporary password');
    } finally {
      setResending(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const result = await mfaNewPassword(id, session, newPassword);
      setSession(result.session);
      setTotpSecret(result.totpSecretCode);

      // Generate QR code
      const email = user?.email || 'user';
      const otpauthUri = `otpauth://totp/MCP%20Atlas:${encodeURIComponent(email)}?secret=${result.totpSecretCode}&issuer=MCP%20Atlas`;
      const dataUrl = await QRCode.toDataURL(otpauthUri, { width: 256 });
      setQrDataUrl(dataUrl);

      setStep('scanQrCode');
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (!/^\d{6}$/.test(totpCode)) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await mfaVerifyTotp(id, session, totpCode);
      setStep('complete');
    } catch (err: any) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>MFA Setup</h1>
      <p style={styles.description}>
        Your access request <strong>#{id}</strong> has been approved. Complete the
        steps below to set up your MCP account with TOTP multi-factor authentication.
      </p>

      {/* Progress indicator */}
      <div style={styles.progress}>
        {(['enterTempPassword', 'setNewPassword', 'scanQrCode', 'complete'] as Step[]).map((s, i) => (
          <div key={s} style={styles.progressStep}>
            <div style={{
              ...styles.progressDot,
              ...(stepIndex(step) >= i ? styles.progressDotActive : {}),
            }}>
              {stepIndex(step) > i ? '\u2713' : i + 1}
            </div>
            <span style={{
              ...styles.progressLabel,
              ...(stepIndex(step) >= i ? styles.progressLabelActive : {}),
            }}>
              {['Verify', 'Password', 'QR Code', 'Done'][i]}
            </span>
          </div>
        ))}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Step 1: Enter Temporary Password */}
      {step === 'enterTempPassword' && (
        <div style={styles.card}>
          <h2 style={styles.stepTitle}>Enter Temporary Password</h2>
          <p style={styles.stepText}>
            Enter the temporary password from your approval email to verify your identity.
          </p>
          <form onSubmit={handleInitAuth}>
            <input
              type="password"
              placeholder="Temporary password"
              value={tempPassword}
              onChange={e => setTempPassword(e.target.value)}
              style={styles.input}
              required
              autoFocus
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </form>
          {resendSuccess && <div style={styles.successBox}>{resendSuccess}</div>}
          <div style={styles.resendRow}>
            <span style={styles.resendText}>Lost or expired?</span>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              style={styles.resendButton}
            >
              {resending ? 'Sending...' : 'Resend temporary password'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Set New Password */}
      {step === 'setNewPassword' && (
        <div style={styles.card}>
          <h2 style={styles.stepTitle}>Set Your New Password</h2>
          <p style={styles.stepText}>
            Choose a strong password for your MCP account. Must be at least 12 characters
            with uppercase, lowercase, numbers, and symbols.
          </p>
          <form onSubmit={handleNewPassword}>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={styles.input}
              required
              autoFocus
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Setting password...' : 'Set Password & Continue'}
            </button>
          </form>
        </div>
      )}

      {/* Step 3: Scan QR Code & Verify */}
      {step === 'scanQrCode' && (
        <div style={styles.card}>
          <h2 style={styles.stepTitle}>Set Up Authenticator</h2>
          <p style={styles.stepText}>
            Scan the QR code below with your authenticator app (Google Authenticator,
            Authy, 1Password, etc.), then enter the 6-digit code it displays.
          </p>
          {qrDataUrl && (
            <div style={styles.qrContainer}>
              <img src={qrDataUrl} alt="TOTP QR Code" style={styles.qrImage} />
            </div>
          )}
          <details style={styles.secretDetails}>
            <summary style={styles.secretSummary}>Can't scan? Enter this key manually</summary>
            <code style={styles.secretCode}>{totpSecret}</code>
          </details>
          <form onSubmit={handleVerifyTotp}>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              placeholder="6-digit code"
              value={totpCode}
              onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...styles.input, textAlign: 'center', letterSpacing: '0.3em', fontSize: '1.2rem' }}
              required
              autoFocus
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Complete Setup'}
            </button>
          </form>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 'complete' && (
        <div style={styles.card}>
          <div style={styles.successIcon}>&#10003;</div>
          <h2 style={{ ...styles.stepTitle, textAlign: 'center' }}>MFA Setup Complete</h2>
          <p style={{ ...styles.stepText, textAlign: 'center' }}>
            Your MCP account is now secured with TOTP multi-factor authentication.
            You will need your authenticator app each time you sign in.
          </p>
          <div style={styles.footer}>
            <Link to="/my-requests" style={styles.backLink}>
              Back to My Requests
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

function stepIndex(step: Step): number {
  return ['enterTempPassword', 'setNewPassword', 'scanQrCode', 'complete'].indexOf(step);
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: 520,
    margin: '0 auto',
  },
  heading: {
    fontSize: '1.6rem',
    fontWeight: 700,
    marginBottom: '0.5rem',
  },
  description: {
    color: '#555',
    lineHeight: 1.6,
    marginBottom: '1.5rem',
  },
  progress: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
    padding: '0 1rem',
  },
  progressStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  progressDot: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#e0e0e0',
    color: '#999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: 4,
  },
  progressDotActive: {
    background: '#4a6cf7',
    color: '#fff',
  },
  progressLabel: {
    fontSize: '0.75rem',
    color: '#999',
  },
  progressLabelActive: {
    color: '#4a6cf7',
    fontWeight: 600,
  },
  errorBox: {
    background: '#ffeef0',
    border: '1px solid #f5c6cb',
    borderRadius: 6,
    padding: '0.75rem 1rem',
    color: '#721c24',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  card: {
    background: '#fff',
    borderRadius: 8,
    padding: '1.5rem',
    marginBottom: '1.25rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  stepTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#1a1a2e',
  },
  stepText: {
    fontSize: '0.9rem',
    color: '#555',
    lineHeight: 1.6,
    marginBottom: '1rem',
  },
  input: {
    display: 'block',
    width: '100%',
    padding: '0.65rem 0.75rem',
    fontSize: '0.95rem',
    border: '1px solid #d0d0d0',
    borderRadius: 6,
    marginBottom: '0.75rem',
    boxSizing: 'border-box',
    outline: 'none',
  },
  button: {
    display: 'block',
    width: '100%',
    padding: '0.7rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#fff',
    background: '#4a6cf7',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  qrContainer: {
    textAlign: 'center',
    margin: '1rem 0',
  },
  qrImage: {
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  secretDetails: {
    marginBottom: '1rem',
  },
  secretSummary: {
    fontSize: '0.85rem',
    color: '#4a6cf7',
    cursor: 'pointer',
    marginBottom: '0.5rem',
  },
  secretCode: {
    display: 'block',
    background: '#f5f5f5',
    padding: '0.75rem',
    borderRadius: 6,
    fontSize: '0.85rem',
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    marginTop: '0.5rem',
  },
  successBox: {
    background: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: 6,
    padding: '0.75rem 1rem',
    color: '#155724',
    fontSize: '0.9rem',
    marginTop: '0.75rem',
  },
  resendRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    marginTop: '1rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #f0f0f0',
  },
  resendText: {
    fontSize: '0.85rem',
    color: '#888',
  },
  resendButton: {
    background: 'none',
    border: 'none',
    color: '#4a6cf7',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  successIcon: {
    fontSize: '3rem',
    color: '#28a745',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  footer: {
    textAlign: 'center',
    paddingTop: '1rem',
  },
  backLink: {
    color: '#4a6cf7',
    fontWeight: 600,
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
};

export default MfaSetup;
